import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { displayToastSuccess } from '@/helpers/display-toast';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { invalidateUserGroupRelatedQueries } from '@/modules/common/utils/query-invalidation';

import { deleteUserGroup } from '../../api/user-groups.api';

export const useDeleteUserGroup = ({
  onSuccess,
}: { onSuccess?: (deletedGroupName: string) => void } = {}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('authenticated.settings');
  const { displayToastErrorFromApiWithFallback } = useApiError();

  return useMutation({
    mutationFn: ({
      id,
      replacementGroupId,
    }: {
      id: number;
      replacementGroupId: number;
      /** Only used for the success toast — the endpoint returns no data */
      name: string;
    }) => deleteUserGroup({ id, replacementGroupId }),
    onSuccess: async (_data, variables) => {
      await invalidateUserGroupRelatedQueries(queryClient);
      displayToastSuccess(t('pages.permissions.toasts.groupDeleted', { name: variables.name }));
      onSuccess?.(variables.name);
    },
    onError: (error: unknown) =>
      displayToastErrorFromApiWithFallback(
        error,
        'authenticated.settings:api.error.userGroups.deleteFailed'
      ),
  });
};
